import { useFormik } from "formik";
import { FC, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import CustomButton from "../../components/UI/Buttons/CustomButton";
import TextInput from "../../components/UI/Inputs/TextInput";
import ErrorMessage from "../../components/UI/Tooltips/ErrorMessage";
import { pathToUserType } from "../../constants/app";
import { TLoginFormValues } from "../../models/form";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../store";
import { resetAuthErrorAction, setShowSignUpDetails } from "../../store/auth/slice";
import { signInThunk } from "../../store/auth/thunks";
import { resetEnquiryErrorAction } from "../../store/enquiry/slice";
import { resetOnboardingErrorAction } from "../../store/onboarding/slice";
import { resetUploadsErrorAction } from "../../store/uploads/slice";
import { createValidationSchema } from "../../utils/createValidationSchema";
import { AUTH_ROUTE, NON_AUTH_ROUTE } from "../App/routes";
import "./Login.scss";

interface LoginPageProps {}

const LoginPage: FC<LoginPageProps> = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const error = useSelector((state) => state.auth.error);
  const inProgress = useSelector((state) => state.auth.inProgress);
  const user = useSelector((state) => state.auth.user);
  const candidateProfileSteps = useSelector((state) => state.onboarding.candidateProfileSteps)
  const personalDetails = useSelector((state) => state.onboarding.personaldetails)

  const [userType, setUserType] = useState<string>('');

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const origin = window.location.origin;
      setUserType(pathToUserType[origin] || '');
    }
  }, []);
  const candidateCompletedStage1 = useMemo(() => (candidateProfileSteps && candidateProfileSteps?.[0]) && 
    ((candidateProfileSteps[0].action === "approve_candidate_to_stage_2" || 
      candidateProfileSteps[0].action === "approve_candidate_bypass_compliance_check") && candidateProfileSteps[0].step_number === 1)
  , [candidateProfileSteps])

  useEffect(() => {
    if(user !== null){
      if(user?.role.id === 5){
        if (
          user.is_connectid_verified === 1 || user.is_profile_complete === 1
        ) {
          if(personalDetails && personalDetails?.verification_status !== "verified" ) {
            navigate(AUTH_ROUTE.MY_PROFILE);
          } else {
            navigate(AUTH_ROUTE.JOBS);
          }
        } else {
          navigate(AUTH_ROUTE.ID_VERIFICATION);
        }
      } else if(user?.role.id === 3 || user?.role.id === 4){
        navigate(AUTH_ROUTE.CLIENT_VACANCIES)
      } else {
        navigate(AUTH_ROUTE.ADMIN_DASHBOARD)
      }
    }
  }, [user, navigate, candidateCompletedStage1, personalDetails])

  // submit the form for login
  const { handleSubmit, errors, touched, handleChange } = useFormik({   
    validateOnBlur: false,
    validateOnChange: false, 
    validationSchema: createValidationSchema("signin"),
    initialValues: {
      email: "",
      password: "",
    },
    onSubmit: async (values: TLoginFormValues) => {
      const payload = { ...values };
      try {
        await dispatch(signInThunk(payload));
      } catch (error) {
        throw new Error(
          "Could not connect to services at the moment. Please try again. Thanks."
        );
      }
    },
  });

  useEffect(() => {
    dispatch(resetAuthErrorAction());
    dispatch(resetOnboardingErrorAction());
    dispatch(resetUploadsErrorAction());
    dispatch(resetEnquiryErrorAction());
  }, [dispatch]);

  const handleCreateEnquiry = () => {
    navigate(NON_AUTH_ROUTE.CREATE_ENQUIRY);
  };

  useEffect(() => {
    if(errors){
      dispatch(resetAuthErrorAction());
    }

    dispatch(setShowSignUpDetails(false))
    sessionStorage.clear()
  }, [dispatch, errors])

  return (
    <>
      <div data-testid="login" className="fs-22 fw-400 text-black mt-20">
        {userType} Login
      </div>
      <form
        className="d-flex flex-column gap-20"
        onSubmit={handleSubmit} // Binds Formik's submit handler to the form
      >
        <TextInput
          type="text"
          field={"email"}
          label={"Email"}
          onChange={handleChange}
          touched={touched.email!}
          error={errors.email}
          placeholder={" " + ""}
        />
        <div className="mt-10">
          <TextInput
            type="password"
            field={"password"}
            label={"Password"}
            onChange={handleChange}
            touched={touched.password!}
            error={errors.password}
            placeholder={" " + ""}
          />
        </div>
        {error && <ErrorMessage message={error?.message} />}
        <div className="d-flex flex-column gap-20 align-items-center">
          <Link
            to={NON_AUTH_ROUTE.FORGOT_PASSWORD}
            className="fs-13 text-grey-medium text-decoration-none"
          >
            Forgot password?
          </Link>
          <CustomButton
            isLoading={inProgress}
            contentClass={"d-flex"}
            type="submit"
            variant="primary"
            className="fw-600 btn-theme bg-teal text-black min-w-250px "
          >
            Login
          </CustomButton>
          <div className="fs-13 text-grey-medium">
            Don't have an account yet?&nbsp;
            <Link to={NON_AUTH_ROUTE.SIGNUP} className="text-grey-medium">
              Create an account
            </Link>
          </div>
          <div className="d-flex d-lg-none fs-13 text-grey-medium">
            Are you a new centre owner?&nbsp;
            <div
              onClick={handleCreateEnquiry}
              className="text-grey-medium text-decoration-underline"
            >
              Submit an enquiry
            </div>
          </div>
          <div className="d-flex d-lg-none gap-10 align-items-center position-relative pt-50px">
            <div className="fs-13 fw-400 text-grey-medium">
              &copy; {new Date().getFullYear()} Connected Crew
            </div>
            <div className="w-1px h-20px bg-grey-medium"></div>
            <Link
              to={NON_AUTH_ROUTE.PRIVACY_POLICY}
              className="fs-13 fw-400 text-grey-medium text-decoration-none"
            >
              Privacy Policy
            </Link>
          </div>
        </div>
      </form>
    </>
  );
};

export default LoginPage;
